import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";
import { Input, type InputProps } from "@/components/ui/input";

// Password field with an inline show/hide eye toggle (UX Screen 14 `.password-toggle`).
// It wraps the existing `Input`, so the FormFieldContext wiring (`aria-invalid` /
// `aria-describedby` / `aria-required`) is inherited unchanged — we only swap the
// input `type` and overlay a token-only toggle button.
//
// The toggle is `type="button"` so it never submits the surrounding form, carries an
// accessible label that flips with state, and exposes `aria-pressed` so assistive tech
// announces whether the password is currently revealed. The entered value is preserved
// across toggles (we change the input type, never its value).
export type PasswordInputProps = Omit<InputProps, "type">;

const PasswordInput = React.forwardRef<HTMLInputElement, PasswordInputProps>(
  ({ className, ...props }, ref) => {
    const [visible, setVisible] = React.useState(false);
    const label = visible ? "Hide password" : "Show password";
    const ToggleIcon = visible ? EyeOff : Eye;

    return (
      <div className="relative">
        <Input
          ref={ref}
          type={visible ? "text" : "password"}
          // Reserve room for the toggle so revealed text never slides under the button.
          className={cn("pr-[44px]", className)}
          {...props}
        />
        <button
          type="button"
          onClick={() => setVisible((v) => !v)}
          // Mirror the field's disabled state so the toggle cannot reveal the masked
          // value of a disabled input.
          disabled={props.disabled}
          aria-label={label}
          aria-pressed={visible}
          title={label}
          // Sits inside the field; its own focus ring is the standard token ring so the
          // input's focus ring is never disturbed.
          className="absolute inset-y-0 right-0 flex w-[44px] items-center justify-center rounded-r-md text-[color:var(--text-tertiary)] transition-colors hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-0 disabled:pointer-events-none disabled:opacity-50"
        >
          <ToggleIcon className="h-[18px] w-[18px]" aria-hidden="true" />
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
